"use client";
import { useState, useEffect, useCallback, useRef } from "react";
import log from "logbeacon";
import type { FlatListItem } from "./types";

interface UseVirtualScrollOptions<T> {
  /** 扁平化的数据列表 */
  flattenedData: FlatListItem<T>[];
  /** 获取项目高度的函数 */
  getHeight: (itemKey: string, item: T, isExpanded: boolean) => number;
  /** 分组标题高度 */
  groupHeaderHeight: number;
  /** 列表项之间的间距 */
  itemGap: number;
  /** 分组之间的间距 */
  groupGap: number;
  /** 分组标题到第一个列表项的间距 */
  groupToItemGap: number;
  /** 当前展开的项目键集合（支持多个同时展开） */
  expandedItemKeys?: Set<string>;
  /** 容器引用 */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** 预渲染缓冲区（上下各渲染多少个额外的项目） */
  overscan?: number;
  /** 是否启用调试模式 */
  debug?: boolean;
}

interface VirtualItem<T> {
  /** 项目类型 */
  type: "header" | "item";
  /** 分组键（header 类型必需） */
  groupKey: string;
  /** 数据项（item 类型必需） */
  data?: T;
  /** 项目键（item 类型必需） */
  itemKey?: string;
  /** 在分组内的索引（item 类型必需） */
  index?: number;
  /** 该项目距离列表顶部的偏移量 */
  offsetTop: number;
  /** 该项目的高度 */
  height: number;
}

interface UseVirtualScrollReturn<T> {
  /** 可见的虚拟项目列表 */
  virtualItems: VirtualItem<T>[];
  /** 列表总高度 */
  totalHeight: number;
  /** 上方占位高度 */
  topPlaceholderHeight: number;
  /** 下方占位高度 */
  bottomPlaceholderHeight: number;
}

/**
 * 虚拟滚动 Hook
 *
 * 根据容器滚动位置和可视高度，计算应该渲染哪些列表项
 */
export function useVirtualScroll<T>(
  options: UseVirtualScrollOptions<T>,
): UseVirtualScrollReturn<T> {
  const {
    flattenedData,
    getHeight,
    groupHeaderHeight,
    itemGap,
    groupGap,
    groupToItemGap,
    expandedItemKeys,
    containerRef,
    overscan = 3,
    debug = false,
  } = options;

  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const rafRef = useRef<number | undefined>(undefined);
  const isFirstGroupRef = useRef<boolean>(true);
  const lastComputedResultRef = useRef<UseVirtualScrollReturn<T> | null>(null);
  const lastVisibleRangeRef = useRef<{ startIndex: number; endIndex: number } | null>(null);

  /**
   * 处理滚动事件
   */
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    if (rafRef.current !== undefined) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      setScrollTop(container.scrollTop);
    });
  }, [containerRef]);

  /**
   * 监听容器滚动和尺寸变化
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // 初始化容器高度
    const updateContainerHeight = () => {
      setContainerHeight(container.clientHeight);
    };

    updateContainerHeight();
    container.addEventListener("scroll", handleScroll, { passive: true });

    // 监听窗口 resize
    const resizeObserver = new ResizeObserver(updateContainerHeight);
    resizeObserver.observe(container);

    return () => {
      if (rafRef.current !== undefined) {
        cancelAnimationFrame(rafRef.current);
      }
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [containerRef, handleScroll]);

  /**
   * 计算虚拟项目列表和布局信息
   */
  const computeVirtualItems = useCallback((): UseVirtualScrollReturn<T> => {
    let currentOffset = 0;
    isFirstGroupRef.current = true;

    // 1. 计算所有项目的位置和高度
    const allItems: VirtualItem<T>[] = [];

    flattenedData.forEach((flatItem) => {
      if (flatItem.type === "header") {
        // 添加分组间距（除了第一个分组）
        if (!isFirstGroupRef.current) {
          currentOffset += groupGap;
        }

        allItems.push({
          type: "header",
          groupKey: flatItem.groupKey,
          offsetTop: currentOffset,
          height: groupHeaderHeight,
        });

        currentOffset += groupHeaderHeight + groupToItemGap;
        isFirstGroupRef.current = false;
      } else if (
        flatItem.type === "item" &&
        flatItem.data &&
        flatItem.itemKey
      ) {
        const isExpanded = expandedItemKeys?.has(flatItem.itemKey) ?? false;
        const itemHeight = getHeight(
          flatItem.itemKey,
          flatItem.data,
          isExpanded,
        );

        allItems.push({
          type: "item",
          groupKey: flatItem.groupKey,
          data: flatItem.data,
          itemKey: flatItem.itemKey,
          index: flatItem.index,
          offsetTop: currentOffset,
          height: itemHeight,
        });

        currentOffset += itemHeight + itemGap;
      }
    });

    const totalHeight = currentOffset;

    // 2. 如果没有容器高度，返回所有项目（初始状态）
    if (containerHeight === 0) {
      return {
        virtualItems: allItems,
        totalHeight,
        topPlaceholderHeight: 0,
        bottomPlaceholderHeight: 0,
      };
    }

    // 3. 计算可视区域
    const viewportStart = scrollTop;
    const viewportEnd = scrollTop + containerHeight;

    // 4. 找到可视区域内的项目（加上 overscan 缓冲区）
    let startIndex = -1;
    let endIndex = -1;

    // 增加一个额外的边界缓冲区，避免边界 item 频繁切换
    const boundaryBuffer = 50; // 50px 的额外缓冲区
    const expandedViewportStart = viewportStart - boundaryBuffer;
    const expandedViewportEnd = viewportEnd + boundaryBuffer;

    for (let i = 0; i < allItems.length; i++) {
      const item = allItems[i];
      const itemTop = item.offsetTop;
      const itemBottom = item.offsetTop + item.height;

      // 找到第一个进入可视区域的项目（使用扩展的视口）
      if (startIndex === -1 && itemBottom > expandedViewportStart) {
        startIndex = Math.max(0, i - overscan);
      }

      // 找到最后一个在可视区域的项目（使用扩展的视口）
      if (itemTop < expandedViewportEnd) {
        endIndex = Math.min(allItems.length - 1, i + overscan);
      }

      // 如果已经超出可视区域，可以提前结束
      if (startIndex !== -1 && itemTop > expandedViewportEnd + overscan * 200) {
        break;
      }
    }

    // 5. 应用粘性边界：如果与上次的范围差异很小，保持上次的范围
    const lastRange = lastVisibleRangeRef.current;
    if (lastRange && startIndex !== -1 && endIndex !== -1) {
      const startDiff = Math.abs(startIndex - lastRange.startIndex);
      const endDiff = Math.abs(endIndex - lastRange.endIndex);
      
      // 如果差异小于 2 个 item，保持上次的范围（避免边界抖动）
      if (startDiff <= 2 && endDiff <= 2) {
        startIndex = lastRange.startIndex;
        endIndex = lastRange.endIndex;
      } else {
        // 更新记录的范围
        lastVisibleRangeRef.current = { startIndex, endIndex };
      }
    } else if (startIndex !== -1 && endIndex !== -1) {
      // 首次计算，记录范围
      lastVisibleRangeRef.current = { startIndex, endIndex };
    }

    // 6. 如果没有找到任何可见项目，返回空列表
    if (startIndex === -1 || endIndex === -1) {
      return {
        virtualItems: [],
        totalHeight,
        topPlaceholderHeight: 0,
        bottomPlaceholderHeight: totalHeight,
      };
    }

    // 7. 提取可见项目
    let visibleItems = allItems.slice(startIndex, endIndex + 1);

    // 7.5. 确保当前视口内所有分组的 header 都被渲染（修复 sticky 定位问题）
    // 收集可见项目中所有的分组
    const visibleGroups = new Set<string>();
    visibleItems.forEach(item => {
      if (item.type === 'item') {
        visibleGroups.add(item.groupKey);
      }
    });

    // 查找这些分组的 header，如果不在可见列表中，则添加进去
    const headersToAdd: VirtualItem<T>[] = [];
    visibleGroups.forEach(groupKey => {
      const hasHeader = visibleItems.some(item => item.type === 'header' && item.groupKey === groupKey);
      if (!hasHeader) {
        // 在所有项目中找到这个分组的 header
        const headerItem = allItems.find(item => item.type === 'header' && item.groupKey === groupKey);
        if (headerItem) {
          headersToAdd.push(headerItem);
        }
      }
    });

    // 将缺失的 header 添加到可见项目中，并按 offsetTop 排序
    if (headersToAdd.length > 0) {
      visibleItems = [...visibleItems, ...headersToAdd].sort((a, b) => a.offsetTop - b.offsetTop);
    }

    // 8. 计算占位高度
    const topPlaceholderHeight =
      startIndex > 0 ? allItems[startIndex].offsetTop : 0;
    const bottomPlaceholderHeight =
      endIndex < allItems.length - 1
        ? totalHeight -
          (allItems[endIndex].offsetTop + allItems[endIndex].height)
        : 0;

    const result = {
      virtualItems: visibleItems,
      totalHeight,
      topPlaceholderHeight,
      bottomPlaceholderHeight,
    };

    // 9. 检查结果是否有实质性变化，避免微小差异导致重复渲染
    const lastResult = lastComputedResultRef.current;
    if (lastResult) {
      const heightThreshold = 5; // 增加到 5px 阈值，更加宽容
      const isSimilar =
        Math.abs(lastResult.topPlaceholderHeight - result.topPlaceholderHeight) <= heightThreshold &&
        Math.abs(lastResult.bottomPlaceholderHeight - result.bottomPlaceholderHeight) <= heightThreshold &&
        Math.abs(lastResult.totalHeight - result.totalHeight) <= heightThreshold &&
        lastResult.virtualItems.length === result.virtualItems.length;

      if (isSimilar) {
        // 如果变化很小，返回上次的结果，避免触发重新渲染
        return lastResult;
      }
    }

    // 缓存当前结果
    lastComputedResultRef.current = result;

    // 10. 输出调试信息
    if (debug && visibleItems.length > 0) {
      log.debug("[文案]🔍 Virtual Scroll Debug:", {
        totalItems: allItems.length,
        visibleItems: visibleItems.length,
        renderPercentage: `${((visibleItems.length / allItems.length) * 100).toFixed(1)}%`,
        scrollPosition: {
          scrollTop,
          containerHeight,
          viewportStart: scrollTop,
          viewportEnd: scrollTop + containerHeight,
        },
        placeholders: {
          top: `${topPlaceholderHeight}px`,
          bottom: `${bottomPlaceholderHeight}px`,
        },
        totalHeight: `${totalHeight}px`,
        overscan,
        expandedItemKeys: expandedItemKeys ? Array.from(expandedItemKeys) : [],
      });
    }

    return result;
  }, [
    flattenedData,
    getHeight,
    groupHeaderHeight,
    itemGap,
    groupGap,
    groupToItemGap,
    expandedItemKeys,
    scrollTop,
    containerHeight,
    overscan,
    debug,
  ]);

  return computeVirtualItems();
}
